import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { revenueActions } from '@nxsys/store/slices/revenueFilings/reducer/revenueSlice';
import {
  GetLeaverToSubmitPSRParams,
  Employment,
} from '@nxsys/store/slices/revenueFilings/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from './ActionsHeader';
import { PSR_SUBMIT_TYPE } from '@nxsys/constants/psr';
import { getColumns } from './columns';
import { showToast } from '@nxsys/utils/toast';
import { Modal } from 'antd';
import AppLoader from '@nxsys/components/AppLoader';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type Props = {
  cbAfterAction?: () => void;
};

const LeaverTab: React.FC<Props> = ({ cbAfterAction }) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { leaverToSubmitPSR } = useSelector(
    (state: AppState) => state.revenueStore,
  );
  const { loading, list, pagination, queryParams } = leaverToSubmitPSR;
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [selectedRows, setSelectedRows] = useState<Employment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      isLeft: true,
      isSentPSR: false,
    });
  }, []);

  const handleFetchData = (params: GetLeaverToSubmitPSRParams) => {
    const payload: GetLeaverToSubmitPSRParams = { ...params };
    payload.companyIds = resolveFilterCompanyIds(
      payload.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(revenueActions.getLeaverToSubmitPSR(payload));
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction?.();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSubmitLeaver = () => {
    if (selectedRows.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.selectAtLeastOneToSubmit'],
      });
      return;
    }

    Modal.confirm({
      title: messages['common.confirm'],
      content: messages['psr.leaver.submitConfirm'],
      onOk: async () => {
        setIsSubmitting(true);
        await dispatch(
          revenueActions.submitLeaverPSR({
            ids: selectedRows.map((row) => row.id),
          }),
        );
        refetchData();
        setIsSubmitting(false);
      },
    });
  };

  const isFiltering = !!queryParams?.search;

  return (
    <>
      {isSubmitting && <AppLoader />}
      <AppsHeader>
        <ActionsHeader
          onFilter={handleFetchData}
          type={PSR_SUBMIT_TYPE.LEAVER}
          onSubmitLeaver={handleSubmitLeaver}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2000, y: 700 }}
          titleEmpty={messages['psr.submit.empty']}
          columns={getColumns({
            messages,
            psrSubmitType: PSR_SUBMIT_TYPE.LEAVER,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selected: any[]) => {
              setSelectedRows(selected as Employment[]);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>
    </>
  );
};

export default LeaverTab;
